import React, {MouseEventHandler, RefObject, useCallback, useState} from 'react'
import {FixedSizeList} from 'react-window'
import {SlctFormat, SlctInternalProps, SlctOption} from 'module/Slct/type/SlctType'
import {CircularProgress, MenuItem, Portal} from '@mui/material'
import {cssSlctEmpty, cssSlctMenu, cssSlctMenuLoadingWrapper} from 'module/Slct/style/SlctStyle'
import {ITEM_SIZE} from 'module/Slct/util/SlctConstant'
import {SlctOptionWrapperComponent} from 'module/Slct/component/SlctOptionWrapperComponent'
import {useDropDownMenuRect} from 'hook/useDropDownMenuRect'
import {css} from '@emotion/react'
import Menu from '@mui/material/Menu'
import {log} from 'common/log'
import {zIndexDropDown} from 'module/Theme/BaseMuiTheme'
import {getSlctOptionCopyText} from 'module/Slct/util/getSlctOptionCopyText'

interface SlctMenuProps<ModelType extends object | void, ValueType extends string | number> {
	menuOpen: boolean
	loading?: boolean
	tabIndex: number
	menuWidth?: number
	menuHeight?: number
	itemData: SlctInternalProps<ModelType, ValueType>
	refWrapper: RefObject<HTMLDivElement | null>
	refInput: RefObject<HTMLInputElement | null>
	refMenu: RefObject<HTMLDivElement | null>
	refList: RefObject<FixedSizeList | null>
	menuPortal: HTMLElement | null | undefined
}

export function SlctMenuComponent<ModelType extends object | void, ValueType extends string | number>(props: SlctMenuProps<ModelType, ValueType>) {
	const {
		menuOpen,
		loading,
		tabIndex,
		menuWidth,
		menuHeight,
		itemData,
		refWrapper,
		refInput,
		refMenu,
		refList,
		menuPortal,
	} = props
	const {options, displayFormat} = itemData
	const optionsLength = itemData.options.length
	const menuPos = useDropDownMenuRect({
		refWrapper,
		open: menuOpen,
		maxHeight: menuHeight || Math.min(optionsLength * ITEM_SIZE, 350) + 10,
		minWidth: menuWidth,
	})

	const onPreserveInputFocus: MouseEventHandler<HTMLDivElement> = useCallback((event) => {
		if (!refInput.current || event.button !== 0) {
			return;
		}
		event.stopPropagation();
		event.preventDefault();
		refInput.current.focus();
	}, [refInput.current])

	const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number, option?: SlctOption<ModelType, ValueType> } | null>(null)
	const openContextMenu = useCallback((evt: React.MouseEvent) => {
		evt.stopPropagation()
		evt.preventDefault() // Verhindert das Standard-Browser-Menü
		const slctOptionEl = (evt.target as HTMLElement).closest('.SlctOptionComponent')
		const valueFromEl = slctOptionEl?.getAttribute('data-value')
		const optionValue = valueFromEl ? JSON.parse(valueFromEl) : null
		const option = options.find(o => o.value === optionValue)
		setContextMenu({
				mouseX: evt.clientX - 10,
				mouseY: evt.clientY - 30,
				option,
			},
		)
	}, [options])
	const closeContextMenu = useCallback(() => {
		setContextMenu(null)
	}, [])
	const option = contextMenu?.option || null
	const copyText = getSlctOptionCopyText(option, displayFormat || SlctFormat.Value)
	const onCopy = useCallback(() => {
		if (!copyText) return;
		void navigator.clipboard.writeText(copyText).catch(x => log.error('Failed to copy slct value', x))
		setContextMenu(null)
	}, [copyText])

	return <>
		<Portal container={menuPortal || document.body}>
			{menuOpen && <div>
				<div
					ref={refMenu}
					tabIndex={tabIndex > 0 ? tabIndex + 1 : tabIndex}
					className={'SlctMenu'}
					onContextMenu={openContextMenu}
					onMouseDown={onPreserveInputFocus}
					css={[
						cssSlctMenu,
						css`
							top: ${menuPos.top}px;
							left: ${menuPos.left}px;
							width: ${menuPos.width}px;
							height: ${menuPos.height}px;
						`,
					]}
				>
					{loading &&
						<div css={cssSlctMenuLoadingWrapper}>
							<CircularProgress color={'inherit'}/>
						</div>
						||
						!optionsLength &&
						<div css={cssSlctEmpty}>
							Keine Einträge
						</div>
						||
						<FixedSizeList
							ref={refList}
							itemData={itemData}
							itemCount={optionsLength}
							itemSize={ITEM_SIZE}
							height={menuPos.height - 2}
							width={menuPos.width - 2}
						>
							{SlctOptionWrapperComponent}
						</FixedSizeList>
					}
				</div>
			</div>
			}
		</Portal>
		<Menu
			open={Boolean(copyText) && contextMenu !== null}
			onClose={closeContextMenu}
			anchorReference="anchorPosition"
			anchorPosition={
				contextMenu !== null
					? {top: contextMenu.mouseY, left: contextMenu.mouseX}
					: undefined
			}
			slotProps={{
				paper: {
					style: {zIndex: zIndexDropDown + 2}, // Muss höher als der Tooltip sein
				},
			}}
		>
			<MenuItem
				onClick={onCopy}
				onContextMenu={closeContextMenu}
			>
				Kopieren: "{copyText}"
			</MenuItem>
		</Menu>
	</>
}